interface FollowParamOptions {
    onMergeSearchParams?: (currentURL: URL, targetURL: URL) => URL;
}

/**
 * Paramètres d'URL suivis par défaut lors de la navigation.
 * Peuvent être étendus via le paramètre extraFollowParams de la fonction initFollowQueryParam.
 */
const DEFAULT_FOLLOW_PARAMS: string[] = [
    "p",
];

/**
 * Domaines sur lesquels faire suivre les paramètres d'URL, en plus du domaine courant.
 * Peuvent être étendus via le paramètre extraWildcardDomains de la fonction initFollowQueryParam
 */
const DEFAULT_WILDCARDS_DOMAINS : string[] = [
    'pcsoft.com',
    'pcsoft.fr',
    'windev.com',
    'windev.es',
    'pcsoft-windev-webdev.com'
]

/**
 * Vérifie si un hostname correspond à un domaine autorisé sous-domaines inclus
 * isAllowedDomain("pcsoft.fr", "pcsoft.fr")        // true
 * isAllowedDomain("doc.pcsoft.fr", "pcsoft.fr")    // true
 * isAllowedDomain("autresite.fr", "pcsoft.fr")     // false
 */
const isAllowedDomain = (hostname: string, allowedDomain: string): boolean => {
    return hostname === allowedDomain || hostname.endsWith(`.${allowedDomain}`);
};

/**
 * Vérifie si l'URL d'un lien est éligible au suivi :
 * - Domaine correspond au site courant
 * - Domaines autorisés (wildcardDomains)
 */
const isFollowableLink = (link: HTMLAnchorElement, wildcardDomains: string[]): boolean => {
    try {
        const { hostname } = new URL(link.href);
        const currentHostname = window.location.hostname;

        return (
            isAllowedDomain(hostname, currentHostname) ||
            wildcardDomains.some(domain => isAllowedDomain(hostname, domain))
        );
    } catch {
        return false;
    }
};

/**
 * Fusionne les paramètres de recherche de deux URLs,
 * en ne transmettant que les paramètres présents dans la whitelist.
 */
const mergeSearchParams = (
    currentURL: URL,
    targetURL: URL,
    followParams: Set<string>,
    onMerge: (currentURL: URL, targetURL: URL) => URL = (_, target) => target
): URL => {
    const mergedParams = new URLSearchParams(targetURL.searchParams);

    currentURL.searchParams.forEach((value, key) => {
        if (!followParams.has(key)) {
            return;
        }

        mergedParams.set(key, value);
    });

    const resultURL = new URL(targetURL.toString());
    resultURL.search = mergedParams.toString();
    return onMerge(currentURL, resultURL);
};

/**
 * Initialise l'élément en fusionnant son URL avec l'URL courante,
 * en ne transmettant que les paramètres de la whitelist.
 * Le hash (#ancre) n'est pas propagé : il est local à la page courante
 * et n'a pas de sens sur une autre page.
 */
export const initFollowParamElement = (
    link: HTMLAnchorElement,
    followParams: Set<string>,
    options: FollowParamOptions = {}
): void => {
    try {
        const currentURL = new URL(window.location.href);
        const targetURL = new URL(link.href);

        const newTargetURL = mergeSearchParams(currentURL, targetURL, followParams, options.onMergeSearchParams);
        link.href = newTargetURL.toString();
    } catch {
        // URL invalide (mailto:, javascript:, ancre relative…)
    }
};

/**
 * Initialise le suivi des paramètres d'URL sur tous les liens internes sauf ceux portant l'attribut data-no-follow-params
 * Seuls les paramètres présents dans la whitelist (DEFAULT_FOLLOW_PARAMS + extraFollowParams) sont transmis.
 * Tous les autres sont ignorés.
 * Il est possible d'ignorer le suivi sur un lien spécifique en lui ajoutant l'attribut : "data-no-follow-params".
 *
 * @param extraFollowParams - Paramètres supplémentaires à suivre dans les URLs
 * @param extraWildcardDomains - Domaines supplémentaires sur lesquels faire suivre les paramètres
 *
 * @example
 * // Utilisation de base (seul "p" est suivi)
 * initFollowQueryParam();
 *
 * @example
 * // Avec des paramètres supplémentaires
 * initFollowQueryParam(["utm_source", "ref"]);
 *
 * @example
 * // Suivi sur un domaine supplémentaire
 * initFollowQueryParam(["utm_source", "ref"], ["pcsoft.com"]); */
export const initFollowQueryParam = (
    extraFollowParams: string[] = [],
    extraWildcardDomains: string[] = [],
): void => {
    const followParams = new Set<string>([...DEFAULT_FOLLOW_PARAMS, ...extraFollowParams]);
    const wildcardDomains = [...DEFAULT_WILDCARDS_DOMAINS, ...extraWildcardDomains];

    document.querySelectorAll<HTMLAnchorElement>('a:not([data-no-follow-params])').forEach(link => {
        if (isFollowableLink(link, wildcardDomains)) {
            link.addEventListener('mousedown', () => {
                initFollowParamElement(link, followParams);
            });
        }
    });
};
